/**
 * User entity — the authenticated storefront user (profile + balance).
 *
 * Mirrors the backend ``MeSerializer`` (``GET /api/me/``): identity, role and the
 * embedded currency balance plus the referral counter shown on Home.
 */
import type { UserBalance } from "../currency";

/** Server-side role enum (RBAC). The storefront is for ``user``; staff use the panel. */
export type UserRole = "user" | "seller" | "manager" | "admin" | "owner";

/**
 * Persisted storefront theme preference (ТЗ §7). Mirrors the backend
 * ``accounts.ThemePreference``; structurally identical to the theme feature's ``Theme``.
 */
export type ThemePreference = "melusine" | "dark" | "oled" | "system";

/**
 * Brand asset URLs served from object storage (MinIO via django-storages),
 * embedded in ``GET /api/me/``. Each value is a URL string; "" means unset
 * (the frontend then renders its built-in fallback glyph).
 */
export interface MeBranding {
  readonly logo: string;
  readonly mora: string;
  readonly primogem: string;
  readonly glitter: string;
  readonly topups: string;
}

/** Current user payload from ``GET /api/me/``. */
export interface Me {
  readonly id: number;
  readonly telegram_id: number;
  readonly username: string;
  readonly first_name: string;
  readonly last_name: string;
  readonly avatar_url: string;
  readonly language: string;
  readonly role: UserRole;
  readonly is_blocked: boolean;
  /** Whether the user has pressed Start in the bot (so we may DM them; дельта §1). */
  readonly bot_started: boolean;
  /** Bot username (no leading ``@``) for the activation / invite deep links (дельта §1). */
  readonly bot_username: string;
  /**
   * Public storefront channel link («Паблик магазина», ТЗ №2 §5). ``""`` when unset —
   * the Home block is then hidden.
   */
  readonly shop_public_url: string;
  /**
   * Human-readable working hours («График работы», ТЗ №7) shown in the shop / support.
   * ``""`` when unset — the schedule block is then hidden.
   */
  readonly shop_hours_text: string;
  /**
   * Whether the shop is currently open (server-resolved: override or МСК clock, ТЗ №7).
   * ``false`` in soft mode merely shows the closed banner — ordering is still allowed; only
   * the ``closed`` override hard-blocks order creation (surfaced as a 400 at checkout).
   */
  readonly shop_is_open: boolean;
  /** Soft-mode banner copy shown while the shop is outside working hours (ТЗ №7). */
  readonly shop_closed_notice: string;
  readonly referrals_count: number;
  /** Invited users with ≥1 completed order (дельта §5). */
  readonly referrals_with_purchases: number;
  /** Cross-device theme choice (server-authoritative; ТЗ §7). */
  readonly theme: ThemePreference;
  readonly balance: UserBalance;
  /** Brand asset URLs from object storage ("" = unset). */
  readonly branding: MeBranding;
}

/** Best display name: first name, else @username, else a neutral fallback. */
export function displayName(me: Pick<Me, "first_name" | "username">): string {
  if (me.first_name.trim().length > 0) {
    return me.first_name;
  }
  if (me.username.trim().length > 0) {
    return `@${me.username}`;
  }
  return "Путешественник";
}
